import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, getToken } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { AppShell } from "../components/AppShell";
import {
  FileSpreadsheet,
  Upload,
  Plus,
  Copy,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Download,
  Loader2,
  ImagePlus,
  Wand2,
  Sparkles,
} from "lucide-react";

/* ───────────── constants ───────────── */
const IMAGE_COLS = [
  "Front Image",
  "Side Image",
  "Back Image",
  "Detail Angle",
  "Look Shot Image",
  "Additional Image 1",
  "Additional Image 2",
];

const TABS = [
  { id: "sizes", label: "Sizes" },
  { id: "brand", label: "Brand" },
  { id: "pricing", label: "Pricing" },
  { id: "category", label: "Category" },
  { id: "attributes", label: "Attributes" },
];

/* ───────────── helpers ───────────── */
const newBlankSKU = () => ({
  _rowId: crypto.randomUUID(),
  vendorSkuCode: "",
  vendorArticleName: "",
  productDisplayName: "",
  brandSize: "",
  standardSize: "",
  prominentColour: "",
  secondColour: "",
  thirdColour: "",
  brandColourRemarks: "",
  mrp: "",
  isp: "",
  productDetails: "",
  styleNote: "",
  images: {}, // { "Front Image": {url, filename} }
});

/* ───────────── small UI atoms ───────────── */
function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#535665] mb-1">
        {label} {required && <span className="text-[#FF3F6C]">*</span>}
      </div>
      {children}
      {hint && <div className="mt-1 text-[10px] text-[#94969F]">{hint}</div>}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", testId }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full px-3 py-2 bg-white border border-[#D4D5D9] rounded-md text-[#282C3F] placeholder:text-[#94969F] text-sm focus:outline-none focus:border-[#FF3F6C]"
    />
  );
}

function TextArea({ value, onChange, placeholder, testId, rows = 2 }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testId}
      className="w-full px-3 py-2 bg-white border border-[#D4D5D9] rounded-md text-[#282C3F] placeholder:text-[#94969F] text-sm focus:outline-none focus:border-[#FF3F6C]"
    />
  );
}

function Select({ value, onChange, options = [], placeholder, testId }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-3 py-2 bg-white border border-[#D4D5D9] rounded-md text-[#282C3F] text-sm focus:outline-none focus:border-[#FF3F6C]"
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/* ───────────── Image upload tile ───────────── */
function ImageTile({ label, asset, onUpload, onClear, required }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(f) {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Image must be ≤ 25 MB");
      return;
    }
    setBusy(true);
    try {
      // Browser-side downscale → eliminates 520 Cloudflare timeouts on
      // large iPhone/Android camera photos.
      const compressed = await compressImage(f);
      const fd = new FormData();
      fd.append("image", compressed);
      const { data } = await api.post("/seller/auto-listing/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      onUpload({
        url: data.url,
        relative_url: data.relative_url,
        asset_id: data.asset_id,
        filename: compressed.name,
      });
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  }

  const previewUrl = asset
    ? `${process.env.REACT_APP_BACKEND_URL}${asset.relative_url}`
    : null;

  return (
    <div
      className="relative rounded-lg border-2 border-dashed border-white/15 bg-black/30 aspect-[3/4] hover:border-[#4F46E5]/50 transition-colors overflow-hidden group"
      data-testid={`image-tile-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      {previewUrl ? (
        <>
          <img
            src={previewUrl}
            alt={label}
            className="w-full h-full object-cover"
          />
          <button
            onClick={onClear}
            className="absolute top-1 right-1 p-1 bg-black/70 rounded hover:bg-black text-white/80"
            data-testid={`clear-image-${label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <Trash2 size={12} />
          </button>
        </>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full h-full flex flex-col items-center justify-center gap-1 text-white/50 hover:text-white/80"
          disabled={busy}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={18} />}
          <span className="text-[10px]">Upload</span>
        </button>
      )}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/90 to-transparent">
        <div className="text-[9px] font-semibold text-white/80 flex items-center gap-1">
          {label}
          {required && <span className="text-[#4F46E5]">*</span>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

/* ───────────── SKU image gallery (left panel) ───────────── */
function SkuImageGallery({ sku, onChange, brand, onAIFill, aiBusy }) {
  const frontImage = sku.images?.["Front Image"];
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/55">
          Images — {sku.vendorSkuCode || "new SKU"}
        </div>
        <button
          onClick={() => onAIFill(frontImage)}
          disabled={aiBusy || !frontImage}
          title={!frontImage ? "Upload the Front Image first" : "AI auto-fills attributes from Front Image"}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gradient-to-r from-[#4F46E5] to-[#4338CA] hover:from-[#4338CA] hover:to-[#CC1F1F] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] font-bold"
          data-testid="ai-autofill-btn"
        >
          {aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          {aiBusy ? "Analyzing…" : "AI Auto-Fill"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {IMAGE_COLS.map((col, idx) => (
          <ImageTile
            key={col}
            label={col}
            required={idx === 0}
            asset={sku.images?.[col]}
            onUpload={(asset) =>
              onChange({ ...sku, images: { ...(sku.images || {}), [col]: asset } })
            }
            onClear={() => {
              const imgs = { ...(sku.images || {}) };
              delete imgs[col];
              onChange({ ...sku, images: imgs });
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ───────────── SKU rows list ───────────── */
function SkuList({ skus, activeIndex, onActivate, onAdd, onDuplicate, onRemove }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/55">
          SKUs ({skus.length})
        </div>
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/15 text-[11px] font-semibold"
          data-testid="add-sku-btn"
        >
          <Plus size={12} /> Add SKU
        </button>
      </div>
      <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1">
        {skus.map((s, i) => (
          <div
            key={s._rowId}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer border ${
              i === activeIndex
                ? "bg-[#4F46E5]/10 border-[#4F46E5]/40"
                : "bg-white/5 border-white/10 hover:border-white/25"
            }`}
            onClick={() => onActivate(i)}
            data-testid={`sku-row-${i}`}
          >
            <div className="flex-1 truncate font-mono-data">
              {s.vendorSkuCode || <span className="text-white/40">(unnamed)</span>}
              <span className="ml-2 text-white/40">{s.brandSize}</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(i);
              }}
              className="p-1 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/10 rounded"
              title="Duplicate"
            >
              <Copy size={11} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(i);
              }}
              className="p-1 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/10 rounded text-red-400"
              title="Remove"
            >
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────── Right panel tabs content ───────────── */
function CommonForm({ common, setCommon, meta, keys, cols = 2 }) {
  if (!meta) return null;
  const fieldMap = Object.fromEntries(meta.common_fields.map((f) => [f.key, f]));
  const fields = keys.map((k) => fieldMap[k]).filter(Boolean);
  return (
    <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>
      {fields.map((f) => (
        <Field key={f.key} label={f.label} required={f.required}>
          {f.options ? (
            <Select
              value={common[f.key]}
              onChange={(v) => setCommon({ ...common, [f.key]: v })}
              options={f.options}
              testId={`common-${f.key}`}
            />
          ) : f.key.endsWith("Description") ? (
            <TextArea
              value={common[f.key]}
              onChange={(v) => setCommon({ ...common, [f.key]: v })}
              placeholder={f.label}
              testId={`common-${f.key}`}
            />
          ) : (
            <TextInput
              value={common[f.key]}
              onChange={(v) => setCommon({ ...common, [f.key]: v })}
              placeholder={f.label}
              testId={`common-${f.key}`}
            />
          )}
        </Field>
      ))}
    </div>
  );
}

function PerSkuForm({ sku, setSku, meta, keys, cols = 2 }) {
  if (!meta) return null;
  const fieldMap = Object.fromEntries(meta.per_sku_fields.map((f) => [f.key, f]));
  const fields = keys.map((k) => fieldMap[k]).filter(Boolean);
  return (
    <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>
      {fields.map((f) => (
        <Field key={f.key} label={f.label} required={f.required}>
          {f.options ? (
            <Select
              value={sku[f.key]}
              onChange={(v) => setSku({ ...sku, [f.key]: v })}
              options={f.options}
              testId={`sku-${f.key}`}
            />
          ) : f.key === "productDetails" || f.key === "styleNote" ? (
            <TextArea
              value={sku[f.key]}
              onChange={(v) => setSku({ ...sku, [f.key]: v })}
              placeholder={f.label}
              testId={`sku-${f.key}`}
            />
          ) : (
            <TextInput
              value={sku[f.key]}
              onChange={(v) => setSku({ ...sku, [f.key]: v })}
              placeholder={f.label}
              type={f.type === "number" ? "number" : "text"}
              testId={`sku-${f.key}`}
            />
          )}
        </Field>
      ))}
    </div>
  );
}

/* ───────────── Sizes quick-add ───────────── */
function SizesQuickAdd({ meta, common, onBulkCreate }) {
  const sizeOptions = useMemo(() => {
    const f = meta?.per_sku_fields.find((x) => x.key === "standardSize");
    return f?.options || [];
  }, [meta]);
  const [selected, setSelected] = useState(new Set());
  const [baseSku, setBaseSku] = useState("");
  const [baseName, setBaseName] = useState("");
  const [mrp, setMrp] = useState("");
  const [isp, setIsp] = useState("");
  const [colour, setColour] = useState("");

  const colourOptions = useMemo(() => {
    const f = meta?.per_sku_fields.find((x) => x.key === "prominentColour");
    return f?.options || [];
  }, [meta]);

  function toggle(s) {
    const next = new Set(selected);
    next.has(s) ? next.delete(s) : next.add(s);
    setSelected(next);
  }

  function createRows() {
    if (selected.size === 0) return toast.error("Pick at least one size");
    if (!baseSku.trim() || !baseName.trim()) {
      return toast.error("Enter base SKU code and article name");
    }
    const sizes = Array.from(selected);
    const rows = sizes.map((s) => ({
      ...newBlankSKU(),
      vendorSkuCode: `${baseSku.trim()}-${s}`,
      vendorArticleName: baseName.trim(),
      productDisplayName:
        (common.brand ? `${common.brand} ` : "") + baseName.trim(),
      brandSize: s,
      standardSize: s,
      prominentColour: colour,
      mrp,
      isp,
    }));
    onBulkCreate(rows);
    setSelected(new Set());
    toast.success(`Added ${rows.length} size variants`);
  }

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Wand2 size={14} className="text-[#4F46E5]" />
          Bulk create size variants
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Base SKU Code" required>
            <TextInput
              value={baseSku}
              onChange={setBaseSku}
              placeholder="KRT-FLORAL-RED"
              testId="bulk-base-sku"
            />
          </Field>
          <Field label="Article Name" required>
            <TextInput
              value={baseName}
              onChange={setBaseName}
              placeholder="Floral Printed Cotton Kurta"
              testId="bulk-article-name"
            />
          </Field>
          <Field label="Prominent Colour">
            <Select
              value={colour}
              onChange={setColour}
              options={colourOptions}
              testId="bulk-colour"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="MRP (₹)">
              <TextInput value={mrp} onChange={setMrp} type="number" testId="bulk-mrp" />
            </Field>
            <Field label="ISP (₹)">
              <TextInput value={isp} onChange={setIsp} type="number" testId="bulk-isp" />
            </Field>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-2">
            Pick sizes
          </div>
          <div className="flex flex-wrap gap-2">
            {sizeOptions.map((s) => (
              <button
                key={s}
                onClick={() => toggle(s)}
                data-testid={`bulk-size-${s}`}
                className={`px-3 py-1 rounded-md text-xs font-semibold border ${
                  selected.has(s)
                    ? "bg-[#4F46E5]/15 border-[#4F46E5]/60 text-white"
                    : "bg-white/5 border-white/15 text-white/70 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={createRows}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-bold"
          data-testid="bulk-create-btn"
        >
          <Plus size={14} /> Create variants
        </button>
      </div>
    </div>
  );
}

/* ───────────── Main page ───────────── */
export default function AutoListing() {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("sizes");
  const [common, setCommon] = useState({
    articleType: "Kurtis",
    countryOfOrigin: "India",
  });
  const [skus, setSkus] = useState([newBlankSKU()]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [metaRes, defaultsRes] = await Promise.all([
          api.get("/seller/auto-listing/meta"),
          api.get("/seller/compliance-defaults").catch(() => ({ data: {} })),
        ]);
        setMeta(metaRes.data);
        // Merge saved defaults INTO `common` so seller never re-types them.
        const defaults = defaultsRes?.data || {};
        setCommon((c) => ({
          ...c,
          ...defaults,
          articleType:
            c.articleType ||
            defaults.articleType ||
            metaRes.data.default_category ||
            "Kurtis",
        }));
      } catch (e) {
        toast.error(errMsg(e, "Failed to load template metadata"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  async function saveAsDefaults() {
    const keys = [
      "brand",
      "manufacturer",
      "packer",
      "importer",
      "countryOfOrigin",
      "hsn",
      "ageGroup",
      "fashionType",
    ];
    const payload = {};
    keys.forEach((k) => {
      if (common[k]) payload[k] = common[k];
    });
    if (Object.keys(payload).length === 0) {
      toast.error("Fill at least one field first");
      return;
    }
    try {
      await api.put("/seller/compliance-defaults", payload);
      toast.success("Saved — these fields auto-fill on every future listing");
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  }

  const activeSku = skus[activeIndex] || skus[0];

  // Smart Pre-Fill: as soon as Front Image + brand are both known, silently
  // start the AI analysis in the background so "AI Auto Fill" feels instant
  // (server dedupes the job, so no double LLM spend).
  const prefillKeyRef = useRef("");
  const prefillAsset = activeSku?.images?.["Front Image"];
  useEffect(() => {
    const brand = (common.brand || "").trim();
    if (!prefillAsset?.asset_id || !brand) return;
    const key = `${prefillAsset.asset_id}|${brand.toLowerCase()}`;
    if (prefillKeyRef.current === key) return;
    const t = setTimeout(() => {
      prefillKeyRef.current = key;
      const fd = new FormData();
      fd.append("brand", brand);
      fd.append("asset_id", prefillAsset.asset_id);
      api
        .post("/seller/auto-listing/analyze-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
        })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [prefillAsset?.asset_id, common.brand]);

  const setActiveSku = useCallback(
    (updater) => {
      setSkus((prev) => {
        const next = [...prev];
        const cur = next[activeIndex];
        next[activeIndex] = typeof updater === "function" ? updater(cur) : updater;
        return next;
      });
    },
    [activeIndex]
  );

  function addSku() {
    setSkus((prev) => [...prev, newBlankSKU()]);
    setActiveIndex(skus.length);
  }
  function removeSku(i) {
    if (skus.length === 1) {
      setSkus([newBlankSKU()]);
      setActiveIndex(0);
      return;
    }
    const next = skus.filter((_, idx) => idx !== i);
    setSkus(next);
    setActiveIndex(Math.min(activeIndex, next.length - 1));
  }
  function duplicateSku(i) {
    const clone = {
      ...skus[i],
      _rowId: crypto.randomUUID(),
      vendorSkuCode: `${skus[i].vendorSkuCode || "SKU"}-copy`,
      images: {}, // don't clone images — they're per-SKU
    };
    const next = [...skus];
    next.splice(i + 1, 0, clone);
    setSkus(next);
    setActiveIndex(i + 1);
  }
  function bulkAdd(rows) {
    // If current single row is blank, replace it; otherwise append
    const cur = skus[0];
    const isBlank = !cur.vendorSkuCode && !cur.vendorArticleName && skus.length === 1;
    const next = isBlank ? rows : [...skus, ...rows];
    setSkus(next);
    setActiveIndex(isBlank ? 0 : skus.length);
  }

  async function aiAutoFill(frontImage) {
    if (!frontImage) {
      toast.error("Upload the Front Image first");
      return;
    }
    if (!common.brand?.trim()) {
      toast.error("Enter the brand name first — AI uses it in the product title");
      return;
    }
    setAiBusy(true);
    try {
      // Prefer the server-side asset_id (tiny POST). Fall back to a full
      // re-upload only when the upload step pre-dates the asset_id field.
      // Fire-and-poll: kick off background job, then poll for the result.
      const fd = new FormData();
      fd.append("brand", common.brand.trim());
      if (frontImage.asset_id) {
        fd.append("asset_id", frontImage.asset_id);
      } else {
        const url = frontImage.url?.startsWith("http")
          ? frontImage.url
          : `${process.env.REACT_APP_BACKEND_URL}${frontImage.relative_url}`;
        const blob = await (await fetch(url)).blob();
        fd.append("image", blob, frontImage.filename || "front.jpg");
      }
      const { data: startResp } = await api.post(
        "/seller/auto-listing/analyze-image",
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 }
      );
      const jobId = startResp.job_id;
      if (!jobId) {
        throw new Error("Server didn't return a job ID");
      }

      // Poll the background job every 400 ms for up to ~76 s — the backend
      // now runs a 4-tier AI waterfall (Gemini → Haiku → Mini → GPT-5.5)
      // that can legitimately take up to ~60 s before its static fallback.
      const POLL_INTERVAL_MS = 400;
      const MAX_POLL_ATTEMPTS = 190;
      let a = null;
      for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const { data: poll } = await api.get(
          `/seller/auto-listing/analyze-image/job/${jobId}`,
          { timeout: 60000 }
        );
        if (poll.status === "done") {
          a = poll.attributes || {};
          break;
        }
        if (poll.status === "failed") {
          throw new Error(poll.error || "AI analysis failed — try again");
        }
      }
      if (!a) {
        throw new Error("AI analysis is taking unusually long — please try again");
      }

      // Apply common fields
      setCommon((c) => ({
        ...c,
        articleType: a.article_type || c.articleType,
        fabric: a.fabric || c.fabric,
        sleeveLength: a.sleeve_length || c.sleeveLength,
        neck: a.neck || c.neck,
        pattern: a.pattern || c.pattern,
        fit: a.fit || c.fit,
        occasion: a.occasion || c.occasion,
        usage: a.usage || c.usage,
        washCare: a.wash_care || c.washCare,
        shape: a.shape || c.shape,
        hemline: a.hemline || c.hemline,
        materialCareDescription:
          a.material_care_description || c.materialCareDescription,
        sizeAndFitDescription:
          a.size_and_fit_description || c.sizeAndFitDescription,
      }));

      // Apply per-SKU fields to the active SKU
      setActiveSku((s) => ({
        ...s,
        vendorArticleName: a.article_name || s.vendorArticleName,
        productDisplayName:
          a.product_display_name || s.productDisplayName,
        prominentColour: a.prominent_colour || s.prominentColour,
        secondColour: a.second_colour || s.secondColour,
        thirdColour: a.third_colour || s.thirdColour,
        productDetails: a.product_details || s.productDetails,
        styleNote: a.style_note || s.styleNote,
      }));

      const filled = Object.keys(a).length;
      toast.success(`AI filled ${filled} attribute${filled === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(errMsg(e, "AI analysis failed"));
    } finally {
      setAiBusy(false);
    }
  }

  function validate() {
    if (!common.brand) return "Brand is required";
    if (!common.manufacturer) return "Manufacturer address is required";
    if (!common.packer) return "Packer address is required";
    if (!common.countryOfOrigin) return "Country of Origin is required";
    if (!common.hsn) return "HSN Code is required";
    if (!common.ageGroup) return "Age Group is required";
    for (let i = 0; i < skus.length; i++) {
      const s = skus[i];
      if (!s.vendorSkuCode) return `SKU #${i + 1}: vendor SKU code missing`;
      if (!s.vendorArticleName) return `SKU #${i + 1}: article name missing`;
      if (!s.productDisplayName) return `SKU #${i + 1}: product display name missing`;
      if (!s.brandSize || !s.standardSize) return `SKU #${i + 1}: size missing`;
      if (!s.prominentColour) return `SKU #${i + 1}: prominent colour missing`;
      if (!s.mrp) return `SKU #${i + 1}: MRP missing`;
    }
    return null;
  }

  // Pre-warm the backend template cache for the selected category so the
  // Generate click returns in ~1-3 s even on a cold pod.
  useEffect(() => {
    const cat = (common.articleType || "").trim();
    if (!cat) return;
    api.post("/seller/auto-listing/warm", { category: cat }).catch(() => {});
  }, [common.articleType]);

  // Auto-download: the moment the Excel is ready, start the browser
  // download automatically — no extra click needed.
  useEffect(() => {
    if (!result?.download_url) return;
    const a = document.createElement("a");
    a.href = `${process.env.REACT_APP_BACKEND_URL}${result.download_url}?token=${getToken()}`;
    a.download = result.filename || "myntra_listing.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // eslint-disable-next-line
  }, [result?.download_url]);

  async function generate() {
    const err = validate();
    if (err) return toast.error(err);

    setGenerating(true);
    setResult(null);
    try {
      // Strip the client-only fields and map images → { col: url }
      const payload = {
        category: common.articleType || "Kurtis",
        common,
        skus: skus.map((s) => {
          const { _rowId, images, ...rest } = s;
          const imgUrls = {};
          Object.entries(images || {}).forEach(([col, asset]) => {
            if (asset?.url) imgUrls[col] = asset.url.startsWith("http")
              ? asset.url
              : `${process.env.REACT_APP_BACKEND_URL}${asset.relative_url}`;
          });
          return { ...rest, images: imgUrls };
        }),
      };
      let { data } = await api.post("/seller/auto-listing/generate", payload, {
        timeout: 60000,
      });
      // Large batches (>50 SKUs) or a busy worker return status=pending —
      // poll the job until the Excel is actually built so the download
      // link is never dead.
      if (data.status === "pending" && data.job_id) {
        const startedAt = Date.now();
        while (Date.now() - startedAt < 120000) {
          const { data: status } = await api.get(
            `/seller/auto-listing/generate/job/${data.job_id}?wait=5`,
            { timeout: 10000 }
          );
          if (status.status === "done") { data = status; break; }
          if (status.status === "failed") {
            throw new Error(status.error || "Excel generation failed");
          }
        }
        if (data.status !== "done") {
          throw new Error("Excel is taking longer than expected — please try again.");
        }
      }
      setResult(data);
      toast.success(`Excel ready — ${data.rows} row(s) · download started`);
    } catch (e) {
      toast.error(errMsg(e, "Generation failed"));
    } finally {
      setGenerating(false);
    }
  }

  const token = getToken();
  const downloadUrl = result
    ? `${process.env.REACT_APP_BACKEND_URL}${result.download_url}?token=${token}`
    : null;

  /* ───────────── render ───────────── */
  if (loading) {
    return (
      <AppShell role="seller">
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 size={16} className="animate-spin" /> Loading template…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell role="seller">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#4F46E5]/10 border border-[#4F46E5]/30 flex items-center justify-center">
            <FileSpreadsheet size={18} className="text-[#4F46E5]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Auto Listing Generator</h1>
            <div className="text-xs text-white/50 mt-0.5">
              Upload images, fill attributes once, download a Myntra-ready Excel for{" "}
              <span className="font-mono-data">{common.articleType || "Kurtis"}</span>
            </div>
          </div>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] disabled:opacity-50 text-white text-sm font-bold"
          data-testid="generate-btn"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
          Generate Excel
        </button>
      </div>

      {result && (
        <div
          className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between"
          data-testid="result-banner"
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 size={18} className="text-emerald-300" />
            <div>
              <div className="text-sm font-semibold text-white">{result.filename}</div>
              <div className="text-[11px] text-white/60">
                {result.rows} row(s) · {result.size_kb} KB
              </div>
            </div>
          </div>
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold"
            data-testid="download-xlsx-btn"
          >
            <Download size={12} /> Download .xlsx
          </a>
        </div>
      )}

      {/* 2-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5">
        {/* LEFT: images + sku list */}
        <div className="space-y-5">
          <div className="p-5 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
            <SkuImageGallery
              sku={activeSku}
              onChange={setActiveSku}
              brand={common.brand}
              aiBusy={aiBusy}
              onAIFill={aiAutoFill}
            />
          </div>
          <div className="p-5 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
            <SkuList
              skus={skus}
              activeIndex={activeIndex}
              onActivate={setActiveIndex}
              onAdd={addSku}
              onDuplicate={duplicateSku}
              onRemove={removeSku}
            />
          </div>
          <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-200/80 flex gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div>
              Images become public URLs referenced inside the Excel so Myntra
              can ingest them. Keep sensitive images off this tool.
            </div>
          </div>
        </div>

        {/* RIGHT: tabs */}
        <div className="p-5 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
          <div className="flex gap-1 mb-5 border-b border-white/10">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px ${
                  tab === t.id
                    ? "border-[#4F46E5] text-white"
                    : "border-transparent text-white/50 hover:text-white/80"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ───── Sizes tab */}
          {tab === "sizes" && (
            <div className="space-y-5">
              <SizesQuickAdd meta={meta} common={common} onBulkCreate={bulkAdd} />
              <div className="p-4 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
                <div className="text-sm font-semibold mb-3">
                  Edit selected SKU · {activeSku.vendorSkuCode || "new"}
                </div>
                <PerSkuForm
                  sku={activeSku}
                  setSku={setActiveSku}
                  meta={meta}
                  keys={[
                    "vendorSkuCode",
                    "vendorArticleName",
                    "productDisplayName",
                    "brandSize",
                    "standardSize",
                    "prominentColour",
                    "secondColour",
                    "thirdColour",
                    "brandColourRemarks",
                  ]}
                />
              </div>
            </div>
          )}

          {/* ───── Brand tab */}
          {tab === "brand" && (
            <div className="space-y-5">
              <CommonForm
                common={common}
                setCommon={setCommon}
                meta={meta}
                keys={[
                  "brand",
                  "articleType",
                  "manufacturer",
                  "packer",
                  "importer",
                  "countryOfOrigin",
                  "hsn",
                  "ageGroup",
                ]}
              />
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-between gap-4">
                <div className="text-[11px] text-emerald-200/80">
                  Save Brand, Manufacturer, Packer, HSN, Country & Age Group as
                  your seller defaults. Every future Auto Listing will pre-fill
                  these — saves ~2 minutes per upload.
                </div>
                <button
                  onClick={saveAsDefaults}
                  className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold"
                  data-testid="save-defaults-btn"
                >
                  <CheckCircle2 size={12} /> Save as my defaults
                </button>
              </div>
            </div>
          )}

          {/* ───── Pricing tab */}
          {tab === "pricing" && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
                <div className="text-sm font-semibold mb-3">
                  Per-SKU pricing · {activeSku.vendorSkuCode || "new"}
                </div>
                <PerSkuForm
                  sku={activeSku}
                  setSku={setActiveSku}
                  meta={meta}
                  keys={["mrp", "isp", "productDetails", "styleNote"]}
                />
              </div>
              <div className="p-4 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10 text-[11px] text-white/55">
                Tip: use the <span className="text-white/80">Sizes</span> tab to
                create all size variants of the same style in one click — each
                inherits MRP/ISP from the bulk form.
              </div>
            </div>
          )}

          {/* ───── Category tab */}
          {tab === "category" && (
            <CommonForm
              common={common}
              setCommon={setCommon}
              meta={meta}
              keys={[
                "fashionType",
                "usage",
                "year",
                "season",
                "occasion",
                "pattern",
                "washCare",
              ]}
            />
          )}

          {/* ───── Attributes tab */}
          {tab === "attributes" && (
            <CommonForm
              common={common}
              setCommon={setCommon}
              meta={meta}
              keys={[
                "fabric",
                "sleeveLength",
                "neck",
                "shape",
                "hemline",
                "materialCareDescription",
                "sizeAndFitDescription",
              ]}
            />
          )}
        </div>
      </div>
    </AppShell>
  );
}
